import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  TaskList,
  CreateTaskListInput,
  UpdateTaskListInput,
  DeleteTaskListAction,
} from '@/types/task-lists';
import { TASK_LIST_OWNED_CAP } from '@/types/task-lists';
import { TaskListsApiClient } from '@/lib/client/TaskListsApiClient';
import { TodoCache } from '@/lib/client/TodoCache';
import { useSupabaseAuth } from '@/lib/supabase';

type UseTaskLists = {
  lists: TaskList[];
  defaultListId: string | null;
  ownedCount: number;
  atCap: boolean;
  loading: boolean;
  createList: (input: CreateTaskListInput) => Promise<TaskList>;
  updateList: (id: string, input: UpdateTaskListInput) => Promise<TaskList>;
  deleteList: (id: string, action: DeleteTaskListAction) => Promise<void>;
  reorderLists: (orderedIds: string[]) => Promise<void>;
  refresh: () => Promise<void>;
  /** Direct setter exposed so coordinators can apply optimistic mutations
   *  alongside their own cache writes without re-fetching. */
  setLists: React.Dispatch<React.SetStateAction<TaskList[]>>;
};

export function useTaskLists(): UseTaskLists {
  const { session } = useSupabaseAuth();
  const [lists, setLists] = useState<TaskList[]>([]);
  const [loading, setLoading] = useState(true);

  const client = useMemo(() => {
    if (!session?.access_token) return null;
    return new TaskListsApiClient(session.access_token);
  }, [session?.access_token]);

  const userId = session?.user?.id ?? null;

  const refresh = useCallback(async () => {
    if (!client || !userId) return;
    try {
      const fetched = await client.listLists();
      setLists(fetched);
      TodoCache.setLists(userId, fetched);
    } catch (error) {
      console.error('[useTaskLists] refresh failed:', error);
    }
  }, [client, userId]);

  // Instant hydrate from cache so the tab bar renders without a network wait.
  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    TodoCache.getLists(userId).then(cached => {
      if (!mounted || !cached || cached.length === 0) return;
      setLists(prev => (prev.length === 0 ? cached : prev));
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [userId]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!client || !userId) {
        setLoading(false);
        return;
      }
      try {
        const fetched = await client.listLists();
        if (mounted) {
          setLists(fetched);
          TodoCache.setLists(userId, fetched);
        }
      } catch (error) {
        console.error('[useTaskLists] initial load failed:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [client, userId]);

  const defaultListId = useMemo(
    () => lists.find(l => l.isDefault && l.ownerUserId === userId)?.id ?? null,
    [lists, userId],
  );

  const ownedCount = useMemo(
    () => lists.filter(l => l.ownerUserId === userId).length,
    [lists, userId],
  );

  const createList = useCallback(
    async (input: CreateTaskListInput) => {
      if (!client) throw new Error('Not authenticated.');
      const created = await client.createList(input);
      setLists(prev => [...prev, created]);
      return created;
    },
    [client],
  );

  const updateList = useCallback(
    async (id: string, input: UpdateTaskListInput) => {
      if (!client) throw new Error('Not authenticated.');
      const previous = lists;
      // Optimistic label/reorder update.
      setLists(prev => prev.map(l => (l.id === id ? { ...l, ...input } as TaskList : l)));
      try {
        const updated = await client.updateList(id, input);
        setLists(prev => prev.map(l => (l.id === id ? updated : l)));
        return updated;
      } catch (error) {
        setLists(previous);
        throw error;
      }
    },
    [client, lists],
  );

  const deleteList = useCallback(
    async (id: string, action: DeleteTaskListAction) => {
      if (!client) throw new Error('Not authenticated.');
      await client.deleteList(id, action);
      setLists(prev => prev.filter(l => l.id !== id));
    },
    [client],
  );

  const reorderLists = useCallback(
    async (orderedIds: string[]) => {
      if (!client) throw new Error('Not authenticated.');
      const previous = lists;
      setLists(prev => {
        const byId = new Map(prev.map(l => [l.id, l]));
        const reordered = orderedIds
          .map((id, idx) => {
            const list = byId.get(id);
            return list ? { ...list, orderIndex: idx } : null;
          })
          .filter((l): l is TaskList => !!l);
        const untouched = prev.filter(l => !orderedIds.includes(l.id));
        return [...reordered, ...untouched];
      });
      try {
        await client.reorderLists(orderedIds);
      } catch (error) {
        setLists(previous);
        throw error;
      }
    },
    [client, lists],
  );

  return {
    lists,
    defaultListId,
    ownedCount,
    atCap: ownedCount >= TASK_LIST_OWNED_CAP,
    loading,
    createList,
    updateList,
    deleteList,
    reorderLists,
    refresh,
    setLists,
  };
}
